import React, { useRef, useEffect, useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { PlusCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { twMerge } from "tailwind-merge";
import { DropdownOption, DropdownList } from "./Dropdown";
import Tooltip from "./Tooltip";

export interface DashedDropdownProps {
  options: DropdownOption[];
  selected?: DropdownOption | DropdownOption[];
  onSelect?: (option: DropdownOption) => void;
  onApply?: (selected: DropdownOption[]) => void;
  onClear?: () => void;
  className?: string;
  label: string;
  active?: boolean;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  border?: boolean;
  listClassName?: string;
  title?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  multiSelect?: boolean;
  virtualized?: boolean;
  isPraetorianUser?: boolean;
  isLoadingOptions?: boolean;
  onDropdownStateChange?: (isOpen: boolean, id: string) => void;
  id?: string;
}

export const DashedDropdown: React.FC<DashedDropdownProps> = ({
  options,
  selected,
  onSelect,
  onApply,
  onClear,
  className,
  label,
  active = false,
  size = "xs",
  border = true,
  listClassName,
  title,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  multiSelect = false,
  virtualized = false,
  isPraetorianUser,
  isLoadingOptions = false,
  onDropdownStateChange,
  id
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [tempSelected, setTempSelected] =
    React.useState<DropdownOption[]>(getTempSelected);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = React.useState(false);

  // Track button position for dropdown placement
  const [buttonPosition, setButtonPosition] = useState<
    | {
        bottom: number;
        left: number;
        width: number;
      }
    | undefined
  >();

  React.useEffect(() => {
    if (isOpen) {
      setTempSelected(getTempSelected());
    }
    // Notify parent about dropdown state change
    if (onDropdownStateChange && id) {
      onDropdownStateChange(isOpen, id);
    }
  }, [isOpen, onDropdownStateChange, id]);

  // Update button position when dropdown opens or window resizes
  useEffect(() => {
    const updatePosition = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setButtonPosition({
          bottom: rect.bottom, // Use viewport-relative coordinate
          left: rect.left, // Use viewport-relative coordinate
          width: rect.width,
        });
      }
    };

    if (isOpen) {
      updatePosition();
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);

      return () => {
        window.removeEventListener("resize", updatePosition);
        window.removeEventListener("scroll", updatePosition, true);
      };
    } else {
      setButtonPosition(undefined);
    }
  }, [isOpen]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setTempSelected(
          multiSelect && Array.isArray(selected)
            ? selected
            : selected
            ? [selected as DropdownOption]
            : []
        );
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside, true);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [isOpen, multiSelect, selected]);

  React.useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const isTextTruncated =
          textRef.current.scrollWidth > textRef.current.clientWidth;
        setIsTruncated(isTextTruncated);
      }
    };

    checkTruncation();
    window.addEventListener("resize", checkTruncation);
    return () => {
      window.removeEventListener("resize", checkTruncation);
    };
  }, [selected]);

  function getTempSelected() {
    return multiSelect && Array.isArray(selected)
      ? selected
      : selected
      ? [selected as DropdownOption]
      : [];
  }

  const handleSelect = (option: DropdownOption) => {
    if (!multiSelect) {
      onSelect?.(option);
      setIsOpen(false);
      return;
    }
  };

  const handleToggleCheckbox = (option: DropdownOption) => {
    if (multiSelect) {
      setTempSelected((prev) => {
        // For groups, toggle all items in the group
        if (option.isGroup && option.items) {
          const groupSelected = option.items.every((item) =>
            prev.some((s) => s.id === item.id)
          );

          if (groupSelected) {
            // Remove all group items (don't add group itself)
            return prev.filter(
              (item) =>
                !option.items?.some((groupItem) => groupItem.id === item.id)
            );
          } else {
            // Add all group items (don't add group itself)
            return [
              ...prev.filter(
                (item) =>
                  !option.items?.some((groupItem) => groupItem.id === item.id)
              ),
              ...option.items,
            ];
          }
        }

        // For individual items
        const isSelected = prev.some((item) => item.id === option.id);
        if (isSelected) {
          // Remove the item
          return prev.filter((item) => item.id !== option.id);
        } else {
          // Add the item
          return [...prev, option];
        }
      });
    }
  };

  const handleApply = () => {
    if (multiSelect && onApply) {
      // Filter out any group items before applying
      const filteredSelection = tempSelected.filter((item) => !item.isGroup);
      onApply(filteredSelection);
    }
    setIsOpen(false);
  };

  const handleClearClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setTempSelected([]);
    onClear?.();
  };

  const sizeClasses = {
    xs: "py-1 px-2 text-xs",
    sm: "py-1.5 px-3 text-sm",
    md: "py-2 px-4 text-sm",
    lg: "py-2.5 px-5 text-base",
    xl: "py-3 px-6 text-base",
  };

  const renderSelectedContent = () => {
    if (!active) return null;

    const getDisplayText = () => {
      if (!multiSelect || !Array.isArray(selected)) {
        return (selected as DropdownOption).label || "";
      }

      // Find complete groups
      const completeGroups = options.filter(
        (option) =>
          option.isGroup &&
          option.items?.every((item) => selected.some((s) => s.id === item.id))
      );

      // Get individual items not part of complete groups
      const individualItems = selected.filter(
        (item) =>
          !item.isGroup && // Don't include groups as individual items
          !completeGroups.some((group) =>
            group.items?.some((groupItem) => groupItem.id === item.id)
          )
      );

      const labels: string[] = [
        ...completeGroups.map((group) => group.label),
        ...individualItems.map((item) => item.label),
      ];

      return labels.join(", ");
    };

    const selectedText = getDisplayText();

    return (
      <>
        <span className="mx-2 text-chariot-brand-primary">|</span>
        {isTruncated ? (
          <Tooltip
            message={
              <div className="w-full min-w-[200px]">
                {multiSelect && Array.isArray(selected) ? (
                  <ul className="space-y-1">
                    {selected
                      .filter((item) => !item.isGroup)
                      .map((option, index) => (
                        <li key={option.id ?? index} className="text-sm">
                          {option.label}
                        </li>
                      ))}
                  </ul>
                ) : (
                  <span className="text-sm">{selectedText}</span>
                )}
              </div>
            }
            position="bottom"
          >
            <span ref={textRef} className="truncate max-w-[200px] inline-block">
              {selectedText}
            </span>
          </Tooltip>
        ) : (
          <span ref={textRef} className="truncate max-w-[200px] inline-block">
            {selectedText}
          </span>
        )}
        <ChevronDownIcon className="ml-2 h-4 w-4 text-current shrink-0" />
      </>
    );
  };

  // Update options to properly show checked state
  const processedOptions = React.useMemo(() => {
    return options.map((option) => {
      if (option.isGroup && option.items) {
        // For groups, check if any items are selected
        const hasSelectedItems = option.items.some((item) =>
          tempSelected.some((s) => s.id === item.id)
        );

        return {
          ...option,
          checked: hasSelectedItems,
          items: option.items.map((item) => ({
            ...item,
            checked: tempSelected.some((s) => s.id === item.id),
          })),
        };
      }

      return {
        ...option,
        checked: tempSelected.some((s) => s.id === option.id),
      };
    });
  }, [options, tempSelected]);
  const DROPDOWN_ELEMENT_WIDTH = 400; // Define the fixed width of the dropdown

  const dropdownStyle = React.useMemo(() => {
    if (!isOpen || !buttonPosition) {
      return undefined;
    }

    const {
      bottom: buttonViewportBottom,
      left: buttonViewportLeft,
      width: buttonWidth,
    } = buttonPosition;
    const screenWidth = typeof window !== "undefined" ? window.innerWidth : 0;

    const dropdownTop = buttonViewportBottom + 8; // 8px gap from button's bottom

    // Default: align dropdown's left with button's left
    let calculatedDropdownLeft = buttonViewportLeft;

    // Check if aligning left with button overflows screen right
    if (buttonViewportLeft + DROPDOWN_ELEMENT_WIDTH > screenWidth) {
      // Try aligning dropdown's right edge with button's right edge
      calculatedDropdownLeft =
        buttonViewportLeft + buttonWidth - DROPDOWN_ELEMENT_WIDTH;

      // Check if this new position makes the dropdown overflow screen left
      if (calculatedDropdownLeft < 0) {
        // Position dropdown's right edge with screen's right edge
        calculatedDropdownLeft = screenWidth - DROPDOWN_ELEMENT_WIDTH;
        // Ensure it doesn't go off-screen left if dropdown is wider than screen
        calculatedDropdownLeft = Math.max(0, calculatedDropdownLeft);
      }
    }
    // Final clamp: ensure dropdown is not off-screen to the left
    calculatedDropdownLeft = Math.max(0, calculatedDropdownLeft);

    return {
      top: `${String(dropdownTop)}px`,
      left: `${String(calculatedDropdownLeft)}px`,
      width: `${String(DROPDOWN_ELEMENT_WIDTH)}px`,
      pointerEvents: "auto" as const,
    };
  }, [isOpen, buttonPosition, DROPDOWN_ELEMENT_WIDTH]);

  return (
    <>
      <div className={twMerge("relative", className)}>
        <button
          ref={buttonRef}
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
          data-testid="dashed-dropdown"
          className={twMerge(
            "flex items-center rounded-full border border-dashed transition-colors duration-150 font-bold min-w-0 max-w-full",
            sizeClasses[size],
            active
              ? "border-chariot-brand-primary bg-chariot-background-secondary text-chariot-text-primary"
              : "border-chariot-stroke text-chariot-text-secondary",
            className
          )}
        >
          {active ? (
            <XCircleIcon
              className="mr-2 h-5 w-5 text-chariot-text-primary shrink-0"
              onClick={handleClearClick}
            />
          ) : (
            <PlusCircleIcon className="mr-2 h-5 w-5 text-chariot-text-secondary shrink-0" />
          )}
          <span className="truncate">{label}</span>
          {renderSelectedContent()}
        </button>
      </div>

      {/* Render dropdown outside of parent containers */}
      {isOpen && dropdownStyle && (
        <div
          ref={dropdownRef}
          className="fixed left-0 top-0 w-full h-0 overflow-visible z-9999"
          style={{ pointerEvents: "none" }}
        >
          <div
            className="fixed inset-0 z-40"
            style={{ pointerEvents: "auto" }}
            onClick={() => {
              setIsOpen(false);
              setTempSelected(
                multiSelect && Array.isArray(selected)
                  ? selected
                  : selected
                  ? [selected as DropdownOption]
                  : []
              );
            }}
          />
          <div
            className="absolute z-50"
            style={dropdownStyle} // Apply the calculated style
          >
            <DropdownList
              options={processedOptions}
              onSelect={handleSelect}
              onToggleCheckbox={handleToggleCheckbox}
              className={twMerge(
                "bg-chariot-background-primary rounded border border-chariot-brand-primary shadow-lg mt-2",
                listClassName
              )}
              border={border}
              title={title}
              searchValue={searchValue}
              onSearchChange={onSearchChange}
              searchPlaceholder={searchPlaceholder}
              multiSelect={multiSelect}
              onApply={handleApply}
              virtualized={virtualized}
              isPraetorianUser={isPraetorianUser}
              isLoadingOptions={isLoadingOptions}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default DashedDropdown;
